/**
 * FRONT-2410 — the workspace's Apps policy as the frontend sees it
 * (System Settings → Apps, and the rule shown next to each switch in an
 * app's Configuration). Mirrors `src/apps/workspace-policy.ts`; the backend
 * normalises whatever this sends, so these types are a convenience, not the
 * security boundary.
 */

export type AppRule = 'allowed' | 'blocked';
export type AppRuleKey = 'ask' | 'tools' | 'agents' | 'visitorRunAs' | 'agentActions';

export interface AppWorkspacePolicy {
  agentic: boolean;
  ask: AppRule;
  tools: AppRule;
  agents: AppRule;
  visitorRunAs: AppRule;
  agentActions: AppRule;
  toolAllowlist: string[] | null;
  agentAllowlist: string[] | null;
}

export interface AppPolicyResponse {
  policy: AppWorkspacePolicy;
  usage: Record<AppRuleKey, number>;
  tools: { name: string; description: string }[];
  agents: { id: string; name: string; description: string }[];
  agentsDegraded: boolean;
}

export const APP_POLICY_PATH = '/api/admin/app-policy';
export const appPolicyKeys = { all: ['app-policy'] as const };

export const APP_RULE_ROWS: { key: AppRuleKey; label: string; description: string }[] = [
  { key: 'ask', label: 'Ask EDDY', description: 'A page asks EDDY a question and shows the answer.' },
  { key: 'tools', label: 'EDDY tools', description: 'A page runs EDDY tools — create a task, search files, search the web.' },
  { key: 'agents', label: 'EDDY agents', description: 'A page hands a task to an agent, e.g. look something up in a calendar.' },
  {
    key: 'visitorRunAs',
    label: 'Agents act for the visitor',
    description: "Agents and Ask EDDY use the signed-in visitor's own accounts, not the app owner's.",
  },
  // FRONT-2221: off until an admin allows it — a new way for an app to write.
  {
    key: 'agentActions',
    label: 'Agents take actions',
    description:
      'An agent acting for the visitor may send, create or post — each time only after that visitor approves it on a card.',
  },
];

/** The same answer the backend's `appRuleAllows` gives: the master switch first. Pure — unit tested. */
export function ruleAllows(policy: AppWorkspacePolicy, key: AppRuleKey): boolean {
  return policy.agentic && policy[key] === 'allowed';
}

/** "12 of 31 tools allowed" — null allowlist means every one. Pure — unit tested. */
export function allowlistSummary(list: string[] | null, total: number, noun: string): string {
  if (list === null) return `All ${total} ${noun} allowed`;
  const count = list.filter(Boolean).length;
  return `${Math.min(count, total)} of ${total} ${noun} allowed`;
}

/**
 * What saving this draft would newly block, and for how many apps — shown
 * next to Save so an admin never blocks something live apps use without
 * seeing it first. Only rules that go from allowed to blocked count. Pure —
 * unit tested.
 */
export function newlyBlocked(
  saved: AppWorkspacePolicy,
  draft: AppWorkspacePolicy,
  usage: Record<AppRuleKey, number>,
): { key: AppRuleKey; label: string; apps: number }[] {
  return APP_RULE_ROWS.filter((row) => ruleAllows(saved, row.key) && !ruleAllows(draft, row.key)).map((row) => ({
    key: row.key,
    label: row.label,
    apps: usage[row.key] ?? 0,
  }));
}

/** The ActionBar message for a draft. Pure — unit tested. */
export function saveMessage(blocked: { label: string; apps: number }[]): string | undefined {
  const affected = blocked.filter((b) => b.apps > 0);
  if (affected.length === 0) return undefined;
  const parts = affected.map((b) => `${b.label} (${b.apps} ${b.apps === 1 ? 'app' : 'apps'})`);
  return `Blocking ${parts.join(', ')} stops it in those apps. Their settings are kept and come back if you allow it again.`;
}
